/**
 * The single import point for zod across the monorepo.
 *
 * `z` is re-exported as the unmodified zod namespace, so every runtime value
 * (`z.string()`) and type member (`z.infer`, `z.ZodString`, `z.ZodType`, …) keeps
 * working. We deliberately do NOT mutate or spread `z`: zod exports it as an ESM
 * module namespace object, which is frozen/non-extensible, so `Object.assign(z, …)`
 * throws at runtime under ESM. Spreading (`{ ...z }`) would silently drop the
 * type-space members (`z.infer`, `z.input`, `z.output`, `z.ZodType`, `z.ZodError`),
 * since those are not enumerable properties.
 *
 * The project helpers therefore live NEXT TO `z`, grouped under the `zv` namespace
 * (e.g. `zv.dateRange()`, `zv.email()`) instead of being bolted onto `z`. Import
 * both from `@faceup/validation`:
 *
 * @example
 * ```ts
 * import { z, zv } from '@faceup/validation'
 * const schema = z.object({ email: zv.email(), range: zv.dateRange().nullable() })
 * ```
 */
import moment from 'moment-timezone'
import { z } from 'zod'
import { toEmailAddress } from './emailAddress'
import { isEmail, isIP, isUrl, isValidPhoneNumber } from './primitives'
import { boundedString } from './stringSchemas'
import { EMAIL_MAX_LENGTH, SHORT_STRING_MAX_LENGTH } from './validationConfig'

const momentSchema = z.custom<moment.Moment>(val => moment.isMoment(val) && val.isValid(), {
  error: 'Expected a valid Moment instance',
})

/** Converts between ISO date strings and Moment instances, use on frontend */
const isoDateToMoment = z.codec(z.iso.date(), momentSchema, {
  decode: isoDate => moment.utc(isoDate),
  encode: date => date.clone().locale('en').format('YYYY-MM-DD'),
})

/** Converts between ISO date strings and Date instances, use on backend */
const isoDateToDate = z.codec(z.iso.date(), z.date(), {
  decode: isoDate => new Date(isoDate),
  encode: date => date.toISOString().slice(0, 10),
})

/** Converts between ISO datetime strings and Date instances, use on backend */
const isoDatetimeToDate = z.codec(z.iso.datetime(), z.date(), {
  decode: isoDatetime => new Date(isoDatetime),
  encode: date => date.toISOString(),
})

/**
 * Wraps a string schema to be nullable, treating empty strings as null.
 *
 * @example
 * ```ts
 * const schema = zv.nullableString(z.string().trim().max(500))
 * schema.parse('')    // → null
 * schema.parse(null)  // → null
 * schema.parse('hi')  // → 'hi'
 * ```
 */
const nullableString = (schema: z.ZodString) =>
  z.preprocess(val => (val === '' ? null : val), schema.nullable())

/**
 * Can be used to validate that an array of objects has unique values for a given key.
 *
 * Intentionally outside the `validatorKey` marker path: it takes a caller `message`
 * and emits a value/path-dependent issue, so it owns its message instead of deferring
 * to the global error map.
 *
 * @example
 * ```ts
 * const schema = z.array(z.object({ id: z.string() })).superRefine(zv.uniqueBy('id'))
 * ```
 */
const uniqueBy =
  <T>(key: keyof T, message?: string) =>
  (items: T[], ctx: z.RefinementCtx) => {
    const seen = new Set<unknown>()
    items.forEach((item, index) => {
      const value = item[key]
      if (seen.has(value)) {
        ctx.addIssue({
          code: 'custom',
          message: message ?? `Duplicate ${String(key)}: ${value}`,
          path: [index, key as string],
        })
      }
      seen.add(value)
    })
  }

/**
 * Machine-readable names for our custom validation failures.
 *
 * Our custom validators report failures as a generic `code: 'custom'` issue, so on their
 * own the failures are indistinguishable. We tag each one with its name in
 * `issue.params.validatorKey` (e.g. `'email'`), so the global error map (ENG-1858) can
 * pick the right localized message without the schema hardcoding it - schema-bound
 * `error`/messages would bypass that global path.
 *
 * Only validators with no native zod format need a name. uuid is intentionally NOT here:
 * use native `z.uuidv7()`, whose failures the error map localizes via
 * `issue.code === 'invalid_format'` / `format: 'uuid'`.
 */
export type ValidatorKey = 'email' | 'url' | 'ipAddress' | 'phone' | 'file'

/** Adds a `custom` issue tagged with the stable `validatorKey` marker (no message). */
const addValidatorIssue = (ctx: z.RefinementCtx, validator: ValidatorKey) =>
  ctx.addIssue({ code: 'custom', params: { validatorKey: validator } })

const stringValidator = (
  validator: ValidatorKey,
  isValid: (value: string) => boolean,
  maxLength?: number
) =>
  (maxLength === undefined
    ? z.string().trim()
    : boundedString(maxLength, { allowEmpty: true })
  ).superRefine((value, ctx) => {
    if (!isValid(value)) {
      addValidatorIssue(ctx, validator)
    }
  })

/** Single from/to end: accepts a `Moment`, `Date`, or ISO date string; normalizes to `Moment`. */
const dateInput = z.union([momentSchema, z.date(), z.iso.date()]).transform(value => moment(value))

/**
 * A from/to date range schema.
 *
 * Input: a `Moment`, a native `Date`, or an ISO date string (`YYYY-MM-DD`).
 * Output: both ends normalized to `Moment` instances.
 *
 * This contract matches the values produced by the `DateRangePicker` from
 * `@faceup/form` (which emits `{ from: Moment; to: Moment }`) while still
 * accepting the `Date`/ISO-string shapes seen in form `defaultValues`. Nullability
 * is left to the call-site (`zv.dateRange().nullable()`), mirroring the old
 * `yup.dateRange().nullable()` usage.
 *
 * @example
 * ```ts
 * const schema = z.object({ range: zv.dateRange().nullable() })
 * schema.parse({ range: { from: '2024-01-01', to: new Date() } })
 * // → { range: { from: Moment, to: Moment } }
 * ```
 */
const dateRange = () => z.object({ from: dateInput, to: dateInput })

/**
 * Validates that a value is a `File` instance.
 *
 * Not Node-only - use it wherever `File` values are validated (e.g. browser uploads).
 * Safe to import anywhere: the `File` global is guarded with `typeof File !== 'undefined'`,
 * so in runtimes without a `File` global the schema fails validation instead of throwing a
 * `ReferenceError`.
 */
const file = () =>
  z.custom<File>(value => typeof File !== 'undefined' && value instanceof File, {
    params: { validatorKey: 'file' satisfies ValidatorKey },
  })

/**
 * The project-specific zod helpers, exposed alongside the unmodified `z` namespace.
 * See the module doc comment (frozen ESM namespace) for why these are not bolted onto `z`.
 */
export const zv = {
  /** A trimmed string of at most `maxLength` characters, see `stringSchemas.ts`. */
  boundedString,
  dateRange,
  /** Strict project email (`isEmail`); emits the `email` marker. */
  email: () => stringValidator('email', isEmail, EMAIL_MAX_LENGTH),
  emailAddress: () => stringValidator('email', isEmail, EMAIL_MAX_LENGTH).transform(toEmailAddress),
  /**
   * URL with required protocol + host (`isUrl`); emits the `url` marker. `isUrl` stops at 2 084
   * characters, more than a `STRING` column holds, so the schema leaves the length to the caller.
   */
  url: () => stringValidator('url', isUrl),
  /** IPv4/IPv6 (`isIP`); emits the `ipAddress` marker. */
  ipAddress: () => stringValidator('ipAddress', isIP, SHORT_STRING_MAX_LENGTH),
  /**
   * Possible phone number (libphonenumber); emits the `phone` marker. libphonenumber refuses more
   * than 250 characters, so the cap refuses nothing new.
   */
  phone: () => stringValidator('phone', isValidPhoneNumber, SHORT_STRING_MAX_LENGTH),
  file,
  isoDateToMoment,
  isoDateToDate,
  isoDatetimeToDate,
  nullableString,
  uniqueBy,
}

export { z }
